/**
 * Payroll Management Tab
 * Role-based access:
 * - Owners: Full access - hours, edit, approve, pay rates, totals
 * - Mgr-Sup: View hours only, no pay rates
 * - Accountant: Read-only with pay rates and totals, download
 */
import { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Clock, Users, Download, Check, Edit2, Save, X,
  ChevronLeft, ChevronRight, AlertCircle, CheckCircle, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function PayrollManagementTab({ adminRole }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [payRates, setPayRates] = useState([]);
  const [activeView, setActiveView] = useState('report'); // report, rates
  const [editingRate, setEditingRate] = useState(null);
  const [editingHours, setEditingHours] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    // Get Monday of current week
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(now.setDate(diff)).toISOString().split('T')[0];
  });

  const role = adminRole?.toLowerCase() || '';
  const isOwner = ['owner', 'admin'].includes(role);
  const isAccountant = role === 'accountant';
  const canSeePay = isOwner || isAccountant;
  const canEdit = isOwner;
  const canApprove = isOwner;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/weekly-report', { params: { week_start: weekStart } });
      setReport(res.data);
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error(err.response?.data?.detail || 'Failed to load payroll report');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  const fetchPayRates = useCallback(async () => {
    if (!isOwner) return;
    try {
      const res = await api.get('/payroll/pay-rates');
      setPayRates(res.data.staff || []);
    } catch (err) {
      console.error('Error fetching pay rates:', err);
    }
  }, [isOwner]);

  useEffect(() => {
    fetchReport();
    fetchPayRates();
  }, [fetchReport, fetchPayRates]);

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const handleUpdatePayRate = async (staffId, hourlyRate) => {
    try {
      const res = await api.post('/payroll/pay-rates', { 
        staff_id: staffId, 
        hourly_rate: parseFloat(hourlyRate) 
      });
      if (res.status === 200) {
        toast.success('Pay rate updated');
        setEditingRate(null);
        fetchPayRates();
        fetchReport();
      } else {
        toast.error(res.data.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pay rate');
    }
  };

  const handleEditHours = async (entryId, newHours, reason) => {
    try {
      const res = await api.post('/payroll/edit-hours', { 
        entry_id: entryId, 
        new_hours: parseFloat(newHours), 
        reason 
      });
      if (res.status === 200) {
        toast.success('Hours updated');
        setEditingHours(null);
        fetchReport();
      } else {
        toast.error(res.data.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update hours');
    }
  };

  const handleApprove = async (staffIds) => {
    try {
      const res = await api.post('/payroll/approve-week', { 
        week_start: weekStart, 
        staff_ids: staffIds 
      });
      if (res.status === 200) {
        toast.success(`Approved ${res.data.approved_count} staff`);
        fetchReport();
      } else {
        toast.error(res.data.detail || 'Failed to approve');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await api.get('/payroll/export-csv', { 
        params: { week_start: weekStart },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_${weekStart}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded payroll report');
    } catch (err) {
      toast.error('Failed to download');
    }
  };

  const getWeekEndDate = () => {
    const start = new Date(weekStart);
    start.setDate(start.getDate() + 6);
    return start.toISOString().split('T')[0];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Payroll Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isOwner ? 'Manage pay rates, edit hours, and approve timesheets' : 
             isAccountant ? 'View and download payroll reports' : 
             'View staff hours'}
          </p>
        </div>
        
        {/* View Toggle (Owners only) */}
        {isOwner && (
          <div className="flex gap-2">
            <Button
              variant={activeView === 'report' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('report')}
              data-testid="payroll-weekly-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Weekly Report
            </Button>
            <Button
              variant={activeView === 'rates' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('rates')}
              data-testid="payroll-pay-rates-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Pay Rates
            </Button>
          </div>
        )}
      </div>

      {/* Pay Rates View (Owners only) */}
      {activeView === 'rates' && isOwner && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">Staff Pay Rates</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Set hourly rates for each staff member</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Staff Name</th>
                  <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Role</th>
                  <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Hourly Rate</th>
                  <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Overtime Rate</th>
                  <th className="text-center p-3 font-medium text-gray-700 dark:text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {payRates.map(staff => (
                  <tr key={staff.id} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="p-3 font-medium text-gray-900 dark:text-white">{staff.name}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400 capitalize">{staff.role}</td>
                    <td className="p-3 text-right">
                      {editingRate === staff.id ? (
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={staff.hourly_rate}
                          className="w-24 text-right"
                          id={`rate-${staff.id}`}
                        />
                      ) : (
                        <span className="text-gray-900 dark:text-white">${staff.hourly_rate?.toFixed(2) || '0.00'}</span>
                      )}
                    </td>
                    <td className="p-3 text-right text-gray-600 dark:text-gray-400">
                      ${(staff.overtime_rate || staff.hourly_rate * 1.5)?.toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      {editingRate === staff.id ? (
                        <div className="flex justify-center gap-1">
                          <Button
                            size="sm"
                            onClick={() => {
                              const input = document.getElementById(`rate-${staff.id}`);
                              handleUpdatePayRate(staff.id, input.value);
                            }}
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingRate(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setEditingRate(staff.id)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {payRates.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-gray-500 dark:text-gray-400">
                      No staff members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weekly Report View */}
      {activeView === 'report' && (
        <>
          {/* Week Navigation */}
          <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <div className="text-center">
              <p className="font-semibold text-gray-900 dark:text-white">
                Week of {formatDate(weekStart)} - {formatDate(getWeekEndDate())}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {report?.summary?.all_approved ? (
                  <span className="text-green-600 dark:text-green-400 flex items-center justify-center gap-1">
                    <CheckCircle className="w-4 h-4" /> All Approved
                  </span>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">Pending Approval</span>
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Staff</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{report?.summary?.total_staff || 0}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Regular Hours</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{report?.summary?.total_regular_hours || 0}</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Overtime</span>
              </div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{report?.summary?.total_overtime_hours || 0}</p>
            </div>
            {canSeePay && (
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Total Payroll</span>
                </div>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">${report?.summary?.total_payroll?.toFixed(2) || '0.00'}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {canApprove && report?.staff?.length > 0 && (
              <Button
                onClick={() => handleApprove(report.staff.filter(s => !s.approved).map(s => s.staff_id))}
                disabled={report.summary?.all_approved}
                className="bg-green-600 hover:bg-green-700"
                data-testid="payroll-approve-all-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Approve All
              </Button>
            )}
            {canSeePay && (
              <Button variant="outline" onClick={handleDownload} data-testid="payroll-export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            )}
            <Button variant="outline" onClick={fetchReport} data-testid="payroll-refresh-btn">
              Refresh
            </Button>
          </div>

          {/* Staff Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Staff</th>
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Role</th>
                    <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Regular</th>
                    <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Overtime</th>
                    <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Total Hrs</th>
                    {canSeePay && (
                      <>
                        <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Rate</th>
                        <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Pay</th>
                      </>
                    )}
                    <th className="text-center p-3 font-medium text-gray-700 dark:text-gray-300">Status</th>
                    {canApprove && <th className="text-center p-3 font-medium text-gray-700 dark:text-gray-300">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {report?.staff?.map(staff => (
                    <tr key={staff.staff_id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="p-3 font-medium text-gray-900 dark:text-white">{staff.staff_name}</td>
                      <td className="p-3 text-gray-600 dark:text-gray-400 capitalize">{staff.role}</td>
                      <td className="p-3 text-right text-gray-900 dark:text-white">{staff.regular_hours}</td>
                      <td className="p-3 text-right text-amber-600 dark:text-amber-400">{staff.overtime_hours}</td>
                      <td className="p-3 text-right font-medium text-gray-900 dark:text-white">{staff.total_hours}</td>
                      {canSeePay && (
                        <>
                          <td className="p-3 text-right text-gray-600 dark:text-gray-400">${staff.hourly_rate?.toFixed(2) || '0.00'}</td>
                          <td className="p-3 text-right font-medium text-green-600 dark:text-green-400">${staff.total_pay?.toFixed(2) || '0.00'}</td>
                        </>
                      )}
                      <td className="p-3 text-center">
                        {staff.approved ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs">
                            <AlertCircle className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </td>
                      {canApprove && (
                        <td className="p-3 text-center">
                          {!staff.approved && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleApprove([staff.staff_id])}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {(!report?.staff || report.staff.length === 0) && (
                    <tr>
                      <td colSpan={canSeePay ? (canApprove ? 9 : 8) : (canApprove ? 7 : 6)} className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No time entries for this week</p>
                      </td>
                    </tr>
                  )}
                </tbody>
                {report?.staff?.length > 0 && (
                  <tfoot className="bg-gray-50 dark:bg-gray-700/50 font-medium">
                    <tr>
                      <td className="p-3 text-gray-900 dark:text-white">TOTALS</td>
                      <td className="p-3"></td>
                      <td className="p-3 text-right text-gray-900 dark:text-white">{report.summary.total_regular_hours}</td>
                      <td className="p-3 text-right text-amber-600 dark:text-amber-400">{report.summary.total_overtime_hours}</td>
                      <td className="p-3 text-right text-gray-900 dark:text-white">{report.summary.total_hours}</td>
                      {canSeePay && (
                        <>
                          <td className="p-3"></td>
                          <td className="p-3 text-right text-green-600 dark:text-green-400">${report.summary.total_payroll?.toFixed(2) || '0.00'}</td>
                        </>
                      )}
                      <td className="p-3"></td>
                      {canApprove && <td className="p-3"></td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
